import React from 'react';
import { Globe, Zap, Shield, Clock, ArrowRight, Mail, Copy, Check, X, Video, Play, Film, Layers, Moon, Sun } from 'lucide-react';
import logo from './logo.svg';
import logoDark from './logo_dark.svg';
import { useTheme } from './ThemeContext';

const Dialog = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative shadow-2xl animate-fade-in">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { isDark, toggle: toggleTheme } = useTheme();
  const [contactOpen, setContactOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "Video Streaming",
      description: "Adaptive bitrate streaming with support for HLS and DASH protocols"
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Multi-CDN",
      description: "Distributed through multiple CDN providers for maximum reliability"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "Protected by Cloudflare and similar providers with advanced DDoS mitigation"
    },
    {
      icon: <Film className="w-8 h-8" />,
      title: "Video Processing",
      description: "Automatic transcoding and optimization for all devices"
    }
  ];

  const videoFeatures = [
    {
      title: "Adaptive Streaming",
      description: "Multiple quality levels automatically adjust to viewer's connection speed"
    },
    {
      title: "Global Delivery",
      description: "Content cached at edge locations worldwide for minimal latency"
    },
    {
      title: "Format Support",
      description: "HLS, DASH, and legacy formats supported across all devices"
    },
    {
      title: "Analytics",
      description: "Real-time viewer metrics, bandwidth usage, and quality statistics"
    }
  ];

  const copyEmail = () => {
    navigator.clipboard.writeText('contact@ety.one');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setContactOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'} font-sans transition-colors`}>
      <Dialog isOpen={contactOpen} onClose={() => setContactOpen(false)}>
        <div className={`${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>Get in touch with us at:</p>
          <div className={`flex items-center justify-between ${isDark ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded-lg`}>
            <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>contact@ety.one</span>
            <button
              onClick={copyEmail}
              className={`flex items-center space-x-2 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
            >
              {copied ? (
                <Check className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              <span className="ml-2">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>
      </Dialog>

      {/* Hero Section */}
      <header className="container mx-auto px-6 py-16">
        <nav className="flex justify-between items-center mb-16">
          <div className="flex items-center space-x-2">
            <img className="max-h-10" src={isDark ? logoDark : logo} alt="Streamline Logo" />
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setContactOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all flex items-center shadow-lg"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </button>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${isDark ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-600'} hover:opacity-90 transition-colors`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl font-bold mb-6">
            Enterprise Media CDN
          </h1>
          <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-8`}>
            High-performance video, audio and image delivery network optimized for streaming content.
            Supporting HLS and DASH protocols with multi-CDN architecture for maximum reliability.
          </p>
          <div className="flex justify-center">
            <button 
              onClick={() => setContactOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all flex items-center shadow-lg"
            >
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className={`container mx-auto px-6 py-24 ${isDark ? 'bg-gray-900' : 'bg-white'} rounded-3xl shadow-2xl transition-colors`}>
        <h2 className="text-4xl font-bold text-center mb-16">
          Video Delivery Infrastructure
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className={`${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'} p-6 rounded-xl transition-all shadow-sm hover:shadow-md`}
            >
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Video Features Section */}
      <section className="container mx-auto px-6 py-24">
        <h2 className="text-4xl font-bold text-center mb-16">
          Streaming Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {videoFeatures.map((feature, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                <Play className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-5xl font-bold text-blue-500 mb-2">99%</div>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Uptime Guarantee</div>
          </div>
          <div>
            <div className="text-5xl font-bold text-blue-500 mb-2">100TB+</div>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Storage Capacity</div>
          </div>
          <div>
            <div className="text-5xl font-bold text-blue-500 mb-2">80ms</div>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Average Latency</div>
          </div>
        </div>
      </section>
      <div className={`text-center ${isDark ? 'text-gray-500' : 'text-gray-600'} py-6`}>By eternity software (https://ety.one). Legal available on https://documents.ety.one</div>
    </div>
  );
};

export default LandingPage;
